import { useEffect, useState, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import ClickAwayListener from '@mui/material/ClickAwayListener';

// Available actions to be displayed in the dropdown
// Icons now use theme instead of direct dark mode props
export const availableActions = [
	{
		id: 'research',
		name: 'research',
		description: 'Research a topic or entity',
		icon: <SearchIcon fontSize='small' />,
	},
	{
		id: 'todo',
		name: 'todo',
		description: 'Create a task to follow up on',
		icon: <CheckBoxOutlinedIcon fontSize='small' />,
	},
	{
		id: 'reminder',
		name: 'reminder',
		description: 'Set a timed reminder',
		icon: <NotificationsActiveOutlinedIcon fontSize='small' />,
	},
];

const ActionDropdown = ({ options, onSelect, onClose }) => {
	const theme = useTheme();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const listRef = useRef(null);
	const selectedItemRef = useRef(null);

	// Handle keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (options.length === 0) return;

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setSelectedIndex((prev) => (prev + 1) % options.length);
					break;
				case 'ArrowUp':
					e.preventDefault();
					setSelectedIndex(
						(prev) => (prev - 1 + options.length) % options.length
					);
					break;
				case 'Enter':
				case 'Tab':
					e.preventDefault();
					if (options[selectedIndex]) {
						onSelect(options[selectedIndex]);
					}
					break;
				case 'Escape':
					e.preventDefault();
					if (onClose) onClose();
					break;
				default:
					break;
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [options, selectedIndex, onSelect, onClose]);

	// Scroll selected item into view when it changes
	useEffect(() => {
		if (selectedItemRef.current && listRef.current) {
			selectedItemRef.current.scrollIntoView({ block: 'nearest' });
		}
	}, [selectedIndex]);

	return (
		<ClickAwayListener onClickAway={() => { if (onClose) onClose(); }}>
			<Paper
				elevation={3}
				sx={{
					width: '100%',
					maxHeight: '300px',
					overflow: 'auto',
					borderRadius: 1,
					backgroundColor: theme.palette.background.paper,
				}}
			>
				{options.length > 0 ? (
					<Box ref={listRef} sx={{ width: '100%' }}>
						{options.map((action, index) => (
						<Box
							component='li'
							key={action.id}
							ref={index === selectedIndex ? selectedItemRef : null}
							onClick={() => onSelect(action)}
							onMouseEnter={() => setSelectedIndex(index)}
							sx={{
								padding: '8px 16px',
								cursor: 'pointer',
								display: 'flex',
								alignItems: 'center',
								backgroundColor:
									index === selectedIndex
										? theme.palette.action.selected
										: 'transparent',
								'&:hover': {
									backgroundColor: theme.palette.action.hover,
								},
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									width: '100%',
								}}
							>
								<Box
									sx={{
										marginRight: 1,
										color: theme.palette.text.secondary,
										display: 'flex',
										alignItems: 'center',
									}}
								>
									{action.icon}
								</Box>
								<Box>
									<Typography variant='body1' color='textPrimary'>
										{action.name}
									</Typography>
									{action.description && (
										<Typography variant='caption' color='textSecondary'>
											{action.description}
										</Typography>
									)}
								</Box>
							</Box>
						</Box>
					))}
				</Box>
				) : (
					<Box
						sx={{
							p: 2,
							color: theme.palette.text.secondary,
							textAlign: 'center',
						}}
					>
						Type to search...
					</Box>
				)}
			</Paper>
		</ClickAwayListener>
	);
};

export default ActionDropdown;
